import React, { useEffect, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, RotateCcw, Check, Move } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

const OUTPUT_SIZE = 600;
const MAX_ZOOM = 3;

// Editor crop 1:1 dengan geser (mouse/touch) & zoom; hasil dirender via canvas tanpa distorsi.
export const PhotoCropDialog = ({ file, open, onCancel, onSave, saving }) => {
  const [img, setImg] = useState(null);
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const boxRef = useRef(null);
  const [box, setBox] = useState(300);
  const drag = useRef(null);

  useEffect(() => {
    if (!file) { setSrc(null); setImg(null); return; }
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { setImg(im); setZoom(1); setPos({ x: 0, y: 0 }); };
    im.src = url;
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!open) return;
    const measure = () => { if (boxRef.current) setBox(boxRef.current.clientWidth); };
    const t = setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [open, img]);

  // skala dasar: sisi terpendek gambar memenuhi area crop (cover)
  const base = img ? box / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const dispW = img ? img.naturalWidth * base * zoom : 0;
  const dispH = img ? img.naturalHeight * base * zoom : 0;

  const clamp = useCallback((p, z = zoom) => {
    if (!img) return p;
    const w = img.naturalWidth * base * z, h = img.naturalHeight * base * z;
    const maxX = Math.max(0, (w - box) / 2), maxY = Math.max(0, (h - box) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  }, [img, base, box, zoom]);

  const changeZoom = (z) => {
    const nz = Math.min(MAX_ZOOM, Math.max(1, z));
    setZoom(nz);
    setPos((p) => clamp({ x: p.x * (nz / zoom), y: p.y * (nz / zoom) }, nz));
  };

  const onPointerDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPos(clamp({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) }));
  };
  const onPointerUp = () => { drag.current = null; };
  const onWheel = (e) => { e.preventDefault(); changeZoom(zoom + (e.deltaY < 0 ? 0.1 : -0.1)); };

  const reset = () => { setZoom(1); setPos({ x: 0, y: 0 }); };

  const save = () => {
    if (!img) return;
    const scale = base * zoom; // px tampilan per px sumber
    const srcSize = box / scale;
    const cx = img.naturalWidth / 2 - pos.x / scale;
    const cy = img.naturalHeight / 2 - pos.y / scale;
    const sx = cx - srcSize / 2, sy = cy - srcSize / 2;
    const out = Math.min(OUTPUT_SIZE, Math.round(srcSize));
    const canvas = document.createElement("canvas");
    canvas.width = out; canvas.height = out;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, out, out);
    canvas.toBlob((blob) => onSave(new File([blob], "foto-jemaat.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-2xl max-w-md w-[calc(100vw-1.5rem)] p-4 sm:p-6" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle>Atur Foto Jemaat</DialogTitle>
          <DialogDescription>Geser foto agar wajah berada di tengah, lalu atur zoom. Area persegi adalah bagian yang akan disimpan.</DialogDescription>
        </DialogHeader>
        <div ref={boxRef} className="relative w-full aspect-square rounded-2xl overflow-hidden bg-slate-900 touch-none select-none cursor-grab active:cursor-grabbing" style={{ touchAction: "none" }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onPointerLeave={onPointerUp} onWheel={onWheel} data-testid="crop-area">
          {src && img && (
            <img src={src} alt="preview" draggable={false} className="absolute pointer-events-none max-w-none"
              style={{ width: dispW, height: dispH, left: `calc(50% + ${pos.x}px)`, top: `calc(50% + ${pos.y}px)`, transform: "translate(-50%, -50%)" }} data-testid="crop-image" />
          )}
          <div className="absolute inset-0 pointer-events-none border-2 border-white/80 rounded-2xl" />
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.25) 1px, transparent 1px)", backgroundSize: "33.33% 33.33%" }} />
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] text-white/80 bg-black/40 px-2 py-1 rounded-full flex items-center gap-1 pointer-events-none"><Move className="h-3 w-3" /> Geser untuk mengatur posisi</div>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <Button type="button" variant="outline" size="icon" className="rounded-xl h-11 w-11 shrink-0" onClick={() => changeZoom(zoom - 0.2)} disabled={zoom <= 1} data-testid="zoom-out-btn" aria-label="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
          <Slider value={[zoom]} min={1} max={MAX_ZOOM} step={0.01} onValueChange={(v) => changeZoom(v[0])} className="flex-1" data-testid="zoom-slider" />
          <Button type="button" variant="outline" size="icon" className="rounded-xl h-11 w-11 shrink-0" onClick={() => changeZoom(zoom + 0.2)} disabled={zoom >= MAX_ZOOM} data-testid="zoom-in-btn" aria-label="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
          <span className="text-xs text-slate-500 w-10 text-right" data-testid="zoom-value">{Math.round(zoom * 100)}%</span>
        </div>
        <DialogFooter className="flex-row gap-2 mt-2">
          <Button type="button" variant="outline" className="rounded-xl min-h-[44px]" onClick={reset} data-testid="crop-reset-btn"><RotateCcw className="h-4 w-4 mr-2" />Reset</Button>
          <Button type="button" variant="outline" className="rounded-xl min-h-[44px] flex-1" onClick={onCancel} disabled={saving} data-testid="crop-cancel-btn">Batal</Button>
          <Button type="button" className="rounded-xl min-h-[44px] flex-1 bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={save} disabled={!img || saving} data-testid="crop-save-btn"><Check className="h-4 w-4 mr-2" />{saving ? "Menyimpan..." : "Simpan Foto"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropDialog;
